import { money, shortDate, signedPct } from "@/lib/format";
import { colors } from "@/lib/theme";
import type { RsiPoint } from "@/lib/types";
import { useElementWidth } from "@/lib/useElementWidth";

const HEIGHT = 140;
const PAD_V = 12;
const MAX_POINTS = 90;

/** 종가 추이를 SVG 라인 차트로 표시(자동 스케일 + 기간 등락률). */
export function PriceChart({ points }: { points: RsiPoint[] }) {
  const [ref, width] = useElementWidth();
  const shown = points.slice(-MAX_POINTS);

  if (shown.length < 2) {
    return (
      <div className="chart" ref={ref}>
        <p className="hint">가격 데이터가 부족합니다.</p>
      </div>
    );
  }

  const closes = shown.map((p) => p.close);
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const span = max - min || 1;
  const plotH = HEIGHT - PAD_V * 2;

  const x = (i: number) => (i / (shown.length - 1)) * width;
  const y = (close: number) => PAD_V + (1 - (close - min) / span) * plotH;

  const linePath = shown
    .map(
      (p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.close).toFixed(1)}`,
    )
    .join(" ");
  const areaPath =
    `${linePath} L ${x(shown.length - 1).toFixed(1)} ${(PAD_V + plotH).toFixed(1)}` +
    ` L 0 ${(PAD_V + plotH).toFixed(1)} Z`;

  const last = shown[shown.length - 1];
  const change = last.close / shown[0].close - 1;
  const lineColor = change >= 0 ? colors.up : colors.down;

  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`종가 추이 ${shortDate(shown[0].date)}~${shortDate(last.date)}, ${signedPct(change)}`}
        >
          <defs>
            <linearGradient id="priceArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={lineColor} stopOpacity={0.25} />
              <stop offset="1" stopColor={lineColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#priceArea)" />
          <path d={linePath} stroke={lineColor} strokeWidth={2} fill="none" />
          <circle
            cx={x(shown.length - 1)}
            cy={y(last.close)}
            r={4}
            fill={lineColor}
          />
        </svg>
      )}

      <div className="chart-axis">
        <span>{shortDate(shown[0].date)}</span>
        <strong style={{ color: lineColor }}>
          {money(last.close)} ({signedPct(change)})
        </strong>
        <span>{shortDate(last.date)}</span>
      </div>
    </div>
  );
}
